import React, { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSwipeable } from 'react-swipeable';
import { map, filter, length, pipe } from 'ramda';
import RegularButton from '../../../../components/RegularButton';
import { IRefReqCounters } from '../../../../common/models/referenceRequest/IRefReqCounters';
import { IBindingAction } from '../../../../common/models/callback';
import { ReferenceType } from '../../../../common/enums/ReferenceType';
import { messages } from './utils';
import { getTotalByType, refTypes } from '../../../../components/ReferenceRoleForm/utils';
import IconButton from '../../../../components/IconButton';
import { ReactComponent as ArrowRight } from '../../../../assets/icons/arrow-right.svg';
import { ReactComponent as ArrowLeft } from '../../../../assets/icons/arrow-left.svg';
import styles from './styles.module.scss';

export interface IOverviewProps {
  refCounters: IRefReqCounters;
  onGoBack: IBindingAction;
  onContinue: IBindingAction;
}

const Overview: FunctionComponent<IOverviewProps> = ({ refCounters, onGoBack, onContinue }) => {
  const count = pipe(
    map((type: ReferenceType) => ({ type, total: getTotalByType(type, refCounters) })),
    filter<{ type: ReferenceType; total: number }, 'array'>(({ total }) => Boolean(total))
  )(refTypes);

  const swipeHandler = useSwipeable({
    onSwipedLeft: onContinue,
    onSwipedRight: onGoBack,
    trackMouse: false
  });

  return (
    <div {...swipeHandler} className="cf-container">
      <p className={`cf-title ${styles.overviewTitle}`}>
        <FormattedMessage
          id="candidateForm.overview.title"
          defaultMessage="Please provide contact information for at least {totalCount} professional references"
          values={{
            totalCount: refCounters.totalCount
          }}
        />
      </p>
      {length(count)
        ? (
          <div className={styles.refCountersContainer}>
            <div className="cf-subtitle text-left mb-3">
              <FormattedMessage id="candidateForm.overview.provided" defaultMessage="Including at least" />
              :
            </div>
            {map(({ type, total }) => {
              const { id, defaultMessage } = messages[type];
              return (
                <div className={styles.item} key={type}>
                  {total}
                  &nbsp;
                  <FormattedMessage id={`${id}.${total > 1 ? 'many' : 'one'}`} defaultMessage={defaultMessage} />
                </div>
              );
            }, count)}
          </div>
        )
        : (
          <div className={`cf-subtitle text-left ${styles.textContainer}`}>
            <FormattedMessage
              id="candidateForm.overview.notProvided"
              defaultMessage={`Your references should ideally be from current or prior professional relationships, 
              including your managers, peers / colleagues, or subordinates.`}
            />
          </div>
        )}
      <div className="cf-footer-btns">
        <IconButton
          size="lg"
          icon={<ArrowLeft />}
          onClick={onGoBack}
        />
        <RegularButton
          size="lg"
          textIntlId="candidateForm.getStarted"
          defaultMessage="Get Started"
          icon={<ArrowRight />}
          iconPosition="right"
          onClick={onContinue}
        />
      </div>
    </div>
  );
};

export default Overview;
